import type { NuxtApp } from '#app';

/**
 * Check if the error is a response error with a 401 status code and log the user out accordingly.
 * Note: The file `log.ts` is not the ideal place, but chose this location because we provide the error handler here.
 * @param nuxtApp
 * @param error
 */
const tryHandle401Error = async (nuxtApp: NuxtApp, error: any) => {
  if (isResponseError(error, 401)) {
    logger.debug('Preventing navigation and showing modal because of invalid token.');

    // Clear auth state
    await nuxtApp.runWithContext(() =>
      useMainStore().resetLogout({ invalidToken: true, reason: 'Invalid token (source: $router.onError)' }),
    );

    // Explicitly enable loading spinner behind error modal
    useMainStore(nuxtApp.$pinia).startLoading();

    // Show error modal with button to login page, so no need to use `abortNavigation` or `navigateTo`
    nuxtApp.$errorHandler.handleErrorModal(error);
  }
};

export default defineNuxtPlugin({
  name: 'log',
  parallel: true,
  setup(_nuxtApp) {
    const nuxtApp = _nuxtApp as NuxtApp;

    /**
     * Handle startup errors - (check link for cases where this callback is triggered)
     * Note: Nuxt already logs these during CSR, but we also manually log it for log prefix consistency.
     * @see https://nuxt.com/docs/getting-started/error-handling#startup-errors
     */
    nuxtApp.hook('app:error', (error) => {
      logger.error('[storefront:error:nuxt]', error);
    });

    /**
     * Vue lifecycle errors - (check link for cases where this callback is triggered)
     * Note: Callback is triggered for unhandled and already handled errors.
     * @see https://nuxt.com/docs/getting-started/error-handling#vue-errors
     */
    nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
      logger.error('[storefront:error:vue]', { error, instance, info });
    };

    nuxtApp.$router.onError(async (error: any, to: any) => {
      logger.error('[storefront:error:router]', { error, to });
      await tryHandle401Error(nuxtApp, error);
    });

    return {
      provide: {
        log: logger,
        errorHandler: new ErrorHandler(),
      },
    };
  },
});
